import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Article } from '../store/api';

type Language = {
  name: string;
  code: string;
  display?: string;
};

const LANGUAGE_SHORT_CODES: Record<string, string> = {
  ENG: 'EN',
  NORSK: 'NO',
  DANSK: 'DK',
  SVENSK: 'SK',
};

export const getLanguageDisplay = (code: string): string =>
  LANGUAGE_SHORT_CODES[code] ?? code;

export type Branding = {
  mainBackground: string;
  boxBackground: string;
  footerBoxBackground: string;
  fontColor: string;
  mainButtonBackground: string;
  mainButtonFont: string;
  secondaryButtonBackground: string;
  secondaryButtonBorder: string;
  secondaryButtonFont: string;
  thirdButtonBackground: string;
  thirdButtonBorder: string;
  thirdButtonFont: string;
  logo: string;
  country?: string | null;
}

type AppState = {
  partnerName: string;
  partnerEmail: string;
  partnerPhone: string;
  resultType: string;
  resultText: string;
  caseId: string;
  cmsData: any;
  languages: Language[];
  selectedLanguage: string;
  termCheck: boolean;
  selectedSourceType: string;
  copiedText: string;
  branding: Branding | null;
  country: string | null;
  accessToken: string;
  articles: Article[];
};

type AppAction = 
  | { type: 'SET_PARTNER'; payload: string }
  | { type: 'SET_PARTNER_EMAIL'; payload: string }
  | { type: 'SET_PARTNER_PHONE'; payload: string }
  | { type: 'SET_RESULT'; payload: { type: string; text: string; caseId: string } }
  | { type: 'SET_CMS_DATA'; payload: any }
  | { type: 'SET_LANGUAGES'; payload: Language[] }
  | { type: 'SET_LANGUAGE'; payload: string }
  | { type: 'SET_TERM_CHECK'; payload: boolean }
  | { type: 'SET_SOURCE_TYPE'; payload: string }
  | { type: 'SET_COPIED_TEXT'; payload: string }
  | { type: 'SET_BRANDING'; payload: Branding | null }
  | { type: 'SET_COUNTRY'; payload: string | null }
  | { type: 'SET_ACCESS_TOKEN'; payload: string }
  | { type: 'SET_ARTICLES'; payload: Article[] };

const initialState: AppState = {
  partnerName: '',
  partnerEmail: '',
  partnerPhone: '',
  resultType: '',
  resultText: '',
  caseId: '',
  cmsData: null,
  languages: [
    { name: 'English', code: 'ENG' },
    { name: 'Norwegian', code: 'NORSK' },
  ],
  selectedLanguage: 'NORSK',
  termCheck: false,
  selectedSourceType: '',
  copiedText: '',
  branding: null,
  country: null,
  accessToken: '',
  articles: [],
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_PARTNER':
      return { ...state, partnerName: action.payload };
    case 'SET_RESULT':
      return { 
        ...state, 
        resultType: action.payload.type,
        resultText: action.payload.text,
        caseId: action.payload.caseId
      };
    case 'SET_CMS_DATA':
      return { ...state, cmsData: action.payload };
    case 'SET_PARTNER_EMAIL':
      return { ...state, partnerEmail: action.payload };
    case 'SET_PARTNER_PHONE':
      return { ...state, partnerPhone: action.payload };
    case 'SET_LANGUAGES':
      return { ...state, languages: action.payload };
    case 'SET_LANGUAGE':
      return { ...state, selectedLanguage: action.payload };
    case 'SET_TERM_CHECK':
      return { ...state, termCheck: action.payload };
    case 'SET_SOURCE_TYPE':
      return { ...state, selectedSourceType: action.payload };
    case 'SET_COPIED_TEXT':
      return { ...state, copiedText: action.payload };
    case 'SET_BRANDING':
      return { ...state, branding: action.payload };
    case 'SET_COUNTRY':
      return { ...state, country: action.payload };
    case 'SET_ACCESS_TOKEN':
      return { ...state, accessToken: action.payload };
    case 'SET_ARTICLES':
      return { ...state, articles: action.payload };
    default:
      return state;
  }
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);